/**
 * Turns the `details.issues` a `validation_failed` response carries (see
 * `AppError.validationFailed` in packages/server/src/errors.ts) into
 * messages a form can show next to the offending field.
 *
 * Bug 1(b) was that the admin threw this information away entirely and
 * showed only the generic "request failed validation" — actionable for
 * nobody. The server's own issue `message` is a mechanical zod string in
 * English ("String must contain at least 12 character(s)"), which is not
 * something we can show verbatim either (every user-visible string goes
 * through i18n — no inline English, see CLAUDE.md). This module maps each
 * issue to a translation key instead of trusting the server's wording.
 */

import type { AdminTranslator } from '../i18n/index.js';
import { ApiError } from './apiClient.js';

export interface FieldIssue {
  path: string;
  message: string;
}

/**
 * The server always responds to a failed zod parse with
 * `{ code, message, details: { issues: [{ path, message }] } }` — see
 * `AppError.validationFailed`. `ApiError.details` is that `details` object.
 */
export function fieldIssuesFromError(err: unknown): FieldIssue[] {
  if (!(err instanceof ApiError)) return [];
  const details = err.details;
  if (details && typeof details === 'object' && 'issues' in details) {
    const issues = (details as { issues: unknown }).issues;
    if (Array.isArray(issues)) {
      return issues.filter(
        (i): i is FieldIssue => typeof i === 'object' && i !== null && typeof (i as FieldIssue).path === 'string',
      );
    }
  }
  return [];
}

/** Extracts the numeric `minimum`/`maximum` a zod `too_small`/`too_big` message embeds, if any. */
function extractBound(message: string): number | null {
  const match = /(\d+(?:\.\d+)?)/.exec(message);
  return match?.[1] ? Number(match[1]) : null;
}

/**
 * Best-effort translation of one server-reported zod issue into a Spanish
 * (or whatever locale is active) message. Falls back to a generic "invalid
 * value" rather than ever showing the server's raw English text.
 */
export function translateFieldIssue(t: AdminTranslator, issue: FieldIssue): string {
  const msg = issue.message;
  if (/at least (\d+) character/.test(msg)) {
    const min = extractBound(msg) ?? 0;
    return t('validation.minLength', { min });
  }
  if (/PIN must be 4 to 6 digits/i.test(msg)) {
    return t('staff.pinInvalid');
  }
  if (/password is required when role is admin/i.test(msg)) {
    return t('staff.passwordRequiredForAdmin');
  }
  if (/invalid email/i.test(msg)) {
    return t('validation.invalidEmail');
  }
  if (/Number must be (less than or equal to|greater than or equal to)/i.test(msg)) {
    return t('validation.outOfRange', { min: 0, max: 100 });
  }
  return t('validation.fieldInvalid');
}

/** Builds a `path -> translated message` map for a set of issues, last one per path wins. */
export function fieldErrorMap(t: AdminTranslator, issues: FieldIssue[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of issues) {
    out[issue.path] = translateFieldIssue(t, issue);
  }
  return out;
}
